import { useTranslation } from "react-i18next";

import { MailIcon, TriangleAlertIcon, UsersIcon } from "lucide-react";

import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";

import type { ChoosableUser } from "../../types";
import type { ExtraContentFunctionProps } from "../UserChooser";

const isChipUser = (user: ChoosableUser): user is ChipUser =>
  !("isEmailInvitee" in user && user.isEmailInvitee);

export const ChallengeAudienceWarning = ({
  extraContentProps,
  externalInvitesInAudience,
  children,
}: {
  extraContentProps: ExtraContentFunctionProps;
  externalInvitesInAudience: boolean;
  children: React.ReactNode;
}) => {
  const cannotViewChosenUsers = extraContentProps.users.filter(
    (c) => !(c.isInAudience || (c.isEmailInvitee && externalInvitesInAudience)),
  );

  if (cannotViewChosenUsers?.length > 0) {
    return (
      <Message color="orange">
        <Icon icon={TriangleAlertIcon} />
        <MessageHeader>{children}</MessageHeader>
        <MessageContent>
          {cannotViewChosenUsers.map((c) => (
            <div key={c._id} style={{ display: "inline-block", margin: 10 }}>
              {isChipUser(c) ? (
                <UserChip
                  user={c}

                  actionClick={() => extraContentProps.unpickUser(c._id)}
                />
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <Icon icon={MailIcon} /> {c.profile.fullName}
                </span>
              )}
            </div>
          ))}
        </MessageContent>
      </Message>
    );
  }
  return null;
};

const AssigneeAudienceWarning = ({ challengeId }: { challengeId: string }) => {
  const { t } = useTranslation();

  return (
    <span>
      The users below are currently outside of the {t("generic.challenge")}{" "}
      audience. Users not in the {t("generic.challenge")} audience will be
      unable to view the {t("generic.challenge")} and their {t("generic.ideas")}
      . To ensure assignees can access their {t("generic.ideas")}, add them to
      the{" "}
      <a
        target="_blank"
        rel="noreferrer"
        href={`/challenges/${challengeId}/settings/audience`}
      >
        {t("generic.challenge")} audience
      </a>{" "}
      after making this assignment.
    </span>
  );
};
ChallengeAudienceWarning.AssigneeAudienceWarning = AssigneeAudienceWarning;

export const AssessorAudienceWarning = () => {
  const { t } = useTranslation();

  return (
    <Message color="blue">
      <MessageHeader>
        <Icon icon={UsersIcon} /> Ensuring assessor access
      </MessageHeader>
      <MessageContent>
        Assigning assessors will create a new assessor {t("generic.group")} and
        add it to the {t("generic.challenge")} audience.
        <br />
        Assessors will be added to the {t("generic.group")} privately, meaning
        they will not be able to see or interact with each other.
      </MessageContent>
    </Message>
  );
};
